import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowUpCircle } from 'lucide-react';
import { useTenant } from '@/providers/tenant-context';
import { useReactNativeBridge, useReactNativeMessageHandler } from '@/hooks/useReactNativeBridge';
import {
  fetchMobileAppVersionCheck,
  isAppUpdateSnoozed,
  parseNativeAppInfo,
  snoozeAppUpdate,
  type NativeAppInfo,
} from '@/lib/mobile-app-update';

interface UpdatePromptState {
  latestVersion: string;
  storeUrl: string | null;
  forceUpdate: boolean;
  nativeInfo: NativeAppInfo;
}

export function MobileAppUpdateProvider({ children }: { children: React.ReactNode }) {
  const { tenant } = useTenant();
  const { isNativeApp, openExternalUrl, requestNativeAppInfo } = useReactNativeBridge();
  const [prompt, setPrompt] = useState<UpdatePromptState | null>(null);
  const pendingNativeInfoRef = useRef<NativeAppInfo | null>(null);
  const appName = tenant?.name ?? 'Milora';

  const evaluateUpdate = useCallback(
    async (nativeInfo: NativeAppInfo) => {
      const tenantSlug = tenant?.slug;
      if (!tenantSlug) {
        pendingNativeInfoRef.current = nativeInfo;
        return;
      }
      console.log('[App Update] Checking version:', { ...nativeInfo, tenantSlug });

      const result = await fetchMobileAppVersionCheck(nativeInfo.platform, nativeInfo.version);

      console.log('[App Update] Version check result:', result);

      if (!result?.updateAvailable || !result.latestVersion) {
        setPrompt(null);
        return;
      }

      if (
        !result.forceUpdate &&
        isAppUpdateSnoozed(tenantSlug, nativeInfo.platform, result.latestVersion)
      ) {
        console.log('[App Update] Update snoozed until later');
        setPrompt(null);
        return;
      }

      console.log('[App Update] Showing update prompt for version', result.latestVersion);
      setPrompt({
        latestVersion: result.latestVersion,
        storeUrl: result.storeUrl,
        forceUpdate: result.forceUpdate,
        nativeInfo,
      });
    },
    [tenant?.slug]
  );

  const nativeAppInfoHandlers = useMemo(
    () => ({
      NATIVE_APP_INFO: (data: { type: string; [key: string]: unknown }) => {
        const nativeInfo = parseNativeAppInfo(data);
        if (!nativeInfo) return;
        void evaluateUpdate(nativeInfo);
      },
    }),
    [evaluateUpdate]
  );

  useReactNativeMessageHandler({
    handlers: nativeAppInfoHandlers,
  });

  useEffect(() => {
    if (!isNativeApp) return;

    requestNativeAppInfo();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestNativeAppInfo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isNativeApp, requestNativeAppInfo]);

  useEffect(() => {
    if (!tenant?.slug) return;
    const pending = pendingNativeInfoRef.current;
    if (!pending) return;

    pendingNativeInfoRef.current = null;
    window.setTimeout(() => {
      void evaluateUpdate(pending);
    }, 0);
  }, [evaluateUpdate, tenant]);

  const handleRemindLater = useCallback(() => {
    if (!prompt || prompt.forceUpdate) return;

    const tenantSlug = tenant?.slug;
    if (!tenantSlug) return;

    snoozeAppUpdate(tenantSlug, prompt.nativeInfo.platform, prompt.latestVersion);
    setPrompt(null);
  }, [prompt, tenant]);

  const handleUpdate = useCallback(() => {
    if (!prompt?.storeUrl) return;
    openExternalUrl(prompt.storeUrl);
  }, [openExternalUrl, prompt]);

  return (
    <>
      {children}
      {isNativeApp && prompt && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (prompt.forceUpdate) return;
            if (!open) handleRemindLater();
          }}
        >
          <DialogContent
            className="sm:max-w-md"
            onPointerDownOutside={(event) => {
              if (prompt.forceUpdate) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (prompt.forceUpdate) event.preventDefault();
            }}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowUpCircle className="h-5 w-5 text-primary" />
                {prompt.forceUpdate ? 'Update required' : 'Update available'}
              </DialogTitle>
              <DialogDescription>
                {prompt.forceUpdate
                  ? `Version ${prompt.latestVersion} is required to continue using ${appName}.`
                  : `Version ${prompt.latestVersion} is available. Update now for the latest features and fixes.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              {!prompt.forceUpdate && (
                <Button type="button" variant="outline" onClick={handleRemindLater}>
                  Remind me later
                </Button>
              )}
              <Button type="button" onClick={handleUpdate} disabled={!prompt.storeUrl}>
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
